import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';

import { CostReviewModalBodyContent } from './cost-review-modal-body-content';
import { messages } from './messages';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { updateSellerSettingsBulk } from '../../../../pages/settings/api/save-settings';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../../wallet/utilities/constants';
import { Providers } from '../../../../enums/providers';
import { hasUpsProviderInCostReview } from '../../../../store/quickship/quickship.helpers';
import { useCurrentCostReviewTrackingId } from '../../../../fulfillment-planner/hooks/useCurrentCostReviewTrackingId';
import { useBalancePurchase } from '../../../../hooks/cost-review/use-balance-purchase';
import { useCombinedCostInfo } from '../../../../hooks/cost-review/use-combined-cost-info';
import { useCostReviewRecommendations } from '../../../../hooks/cost-review/use-cost-review-recommendations';
import { Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { CostReviewModalFooter } from '../../../cost-review/cost-review-modal-footer/cost-review-modal-footer';
import { CostReviewRecommendationBanner } from '../../../cost-review/cost-review-recommendation-banner/cost-review-recommendation-banner';
import { UpsTermsCheckbox } from '../../../ups-terms-checkbox/ups-terms-checkbox';
import { useEnableUpsTerms2 } from '../../../../feature-flags/hooks/use-enable-ups-terms-2';
import { costReviewSubject } from '../../cost-review-modal';
import { LabelBatchLoading } from '../loading';
import sharedStyle from '../style.module.less';
import { usePaymentSurchargePreview } from '../../../../financial-services-ui/surcharge/hooks';

/**
 * @typedef {import('../../../../prop-types/cost-review.types').LabelProviderCostReview} LabelProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').WalletedProviderCostReview} WalletedProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').InsuranceProviderCostReview} InsuranceProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewWarning} CostReviewWarning
 */

/**
 * @param {object} props
 * @param {boolean} props.isLoading
 * @param {LabelProviderCostReview[]} props.providers
 * @param {WalletedProviderCostReview[][]} props.walletedProviderGroups
 * @param {InsuranceProviderCostReview[]} props.insurances
 * @param {(walletId: string, amount: number) => void} props.walletPurchaseAmountChanged
 * @param {(provider: string, amount: number) => void} props.providerPurchaseAmountChanged
 * @param {(provider: string, amount: number) => void} props.insurancePurchaseAmountChanged
 * @param {boolean} props.isContinueDisabled
 * @param {(providers: LabelProviderCostReview[], insurances: InsuranceProviderCostReview[], walletedProviderGroups: WalletedProviderCostReview[][]) => void} props.purchasePostage
 * @param {() => void} props.deferPostagePurchase
 * @param {boolean} props.enoughFunds
 * @param {string} props.shipDate
 * @param {(newShipDate: string) => void} props.onShipDateChanged
 * @param {boolean} props.userHasBillingInfoSetup
 * @param {() => void} props.closeOrderDetailsModal
 * @param {string} props.userDateFormat
 * @param {Array<CostReviewWarning>} props.costWarnings
 * @param {number} props.shipFromCount
 * @param {boolean} props.shouldShowError
 * @param {string} props.error
 * @param {() => void} props.onRetry
 * @param {BulkRateRecommendationSummary} props.recommendationSummary
 * @param {(applyRecommendationsBulkUpdateJobGuid: string, serviceRecommendations: object, insuranceRecommendations: object) => void} props.applyRecommendations
 * @param {boolean} props.isApplyingRecommendations
 * @param {Currency} props.totalAppliedSavings
 * @param {boolean} props.upsTermsRequired
 * @returns {React.JSX.Element}
 */
export const CostReviewBody = ({
  isLoading = false,
  providers = emptyArray,
  walletedProviderGroups = emptyArray,
  insurances = emptyArray,
  walletPurchaseAmountChanged = noop,
  providerPurchaseAmountChanged = noop,
  insurancePurchaseAmountChanged = noop,
  isContinueDisabled = true,
  purchasePostage: purchasePostageProp = noop,
  deferPostagePurchase = noop,
  enoughFunds = false,
  shipDate,
  onShipDateChanged = noop,
  userHasBillingInfoSetup = false,
  closeOrderDetailsModal = noop,
  userDateFormat = 'DD/MM/YYYY',
  costWarnings = emptyArray,
  shipFromCount = 1,
  shouldShowError = false,
  error,
  onRetry = noop,
  recommendationSummary = emptyObject,
  applyRecommendations = noop,
  isApplyingRecommendations = false,
  totalAppliedSavings = emptyObject,
  upsTermsRequired = false,
}) => {
  const dispatch = useDispatch();
  const [upsTermsAccepted, setUpsTermsAccepted] = useState(false);
  const isUpsTermsEnabled = useEnableUpsTerms2();
  const combinedCostInfo = useCombinedCostInfo(providers, insurances, walletedProviderGroups);
  const {
    loading: surchargeLoading,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    error: previewError,
    triggerRetry: triggerPreviewRetry,
    hasCheckedAtLeastOnce: hasSurchargeCheckedAtLeastOnce,
  } = usePaymentSurchargePreview({ total: combinedCostInfo.totalWalletPurchaseAmount });
  const [addFundError, setAddFundError] = useState(undefined);
  const { currentAnalyticsSourceTrackingId } = useCurrentCostReviewTrackingId();
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const isCancel = useRef(true);
  const [disableButton, setDisableButton] = useState(false);
  const onAddFundsComplete = () => {
    isCancel.current = false;
  };

  // CostReview Subject
  useEffect(() => {
    const subscription = costReviewSubject.subscribe(({ costReviewErrorMessage }) => {
      const firstErrorMessage = costReviewErrorMessage[0]?.errorMessage;
      if (firstErrorMessage) {
        setAddFundError(firstErrorMessage);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    return () => {
      if (currentAnalyticsSourceTrackingId && !enoughFunds && isCancel.current) {
        trackAddFunds(WorkflowSteps.CANCEL);
      }
    };
  }, [currentAnalyticsSourceTrackingId, isCancel, enoughFunds, trackAddFunds]);
  useEffect(() => {
    if (currentAnalyticsSourceTrackingId && !enoughFunds && walletedProviderGroups.length) {
      trackAddFunds(WorkflowSteps.START, {
        source: `${currentAnalyticsSourceTrackingId} - Label Batch Cost Review`,
      });
    }
  }, [currentAnalyticsSourceTrackingId, enoughFunds, trackAddFunds, walletedProviderGroups.length]);

  const localDeliveryShipmentIsPresent = useMemo(() => {
    return (
      providers.some((provider) => provider.labelProviderId === Providers.Uber.labelProviderId) ||
      walletedProviderGroups.some((providerGroup) =>
        providerGroup.some((provider) => provider.labelProviderId === Providers.UberWallet.labelProviderId),
      )
    );
  }, [providers, walletedProviderGroups]);

  const hasUpsProviderInCosts = useMemo(
    () => hasUpsProviderInCostReview(providers, walletedProviderGroups),
    [providers, walletedProviderGroups],
  );

  const purchasePostage = useBalancePurchase(purchasePostageProp, trackAddFunds, onAddFundsComplete, closeOrderDetailsModal);

  const proceedWithPurchase = useCallback(() => {
    if (enoughFunds) {
      deferPostagePurchase();
      closeOrderDetailsModal();
      return;
    }
    purchasePostage(providers, insurances, walletedProviderGroups);
  }, [closeOrderDetailsModal, deferPostagePurchase, enoughFunds, insurances, providers, purchasePostage, walletedProviderGroups]);

  const onSubmit = useCallback(async () => {
    setDisableButton(true);
    setAddFundError(undefined);

    if (upsTermsRequired && upsTermsAccepted) {
      try {
        const newSettings = await updateSellerSettingsBulk({ [HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING]: 'true' });
        dispatch(saveNewSellerSettings(newSettings));
      } catch {
        setDisableButton(false);
        setAddFundError('Failed to save UPS terms acceptance. Please try again.');
        return;
      }
    }

    proceedWithPurchase();
  }, [dispatch, proceedWithPurchase, upsTermsRequired, upsTermsAccepted]);

  const shouldDisableContinue =
    disableButton ||
    isContinueDisabled ||
    surchargeLoading ||
    previewError ||
    (isUpsTermsEnabled && upsTermsRequired && hasUpsProviderInCosts && !upsTermsAccepted);

  const transactionPreviewProps = useMemo(
    () => ({
      surchargeLoading,
      creditCardProcessingFee,
      creditCardProcessingFeePercentage,
      previewError,
      triggerPreviewRetry,
      hasSurchargeCheckedAtLeastOnce,
    }),
    [
      surchargeLoading,
      creditCardProcessingFee,
      creditCardProcessingFeePercentage,
      previewError,
      triggerPreviewRetry,
      hasSurchargeCheckedAtLeastOnce,
    ],
  );

  const {
    shippingServiceRecommendations,
    selectedShippingServiceRecommendations,
    toggleShippingServiceRecommendation,
    selectedInsuranceRecommendations,
    insuranceRecommendations,
    toggleInsuranceRecommendation,
    selectedRateShopperRecommendations,
    rateShopperRecommendations,
    toggleRateShopperRecommendation,
    selectAllRecommendations,
    hasSelectedRecommendations,
    applySelectedRecommendations,
  } = useCostReviewRecommendations(recommendationSummary, applyRecommendations);

  if (isLoading) {
    return <LabelBatchLoading />;
  }

  return (
    <>
      <ModalHeader className={sharedStyle['batch-processing-modal-header']}>
        <FormattedMessage {...messages.costReviewTitle} />
      </ModalHeader>
      <ModalBody className={sharedStyle['batch-processing-modal-body']}>
        {addFundError ? <BannerMessageV2 content={addFundError} status={BannerStatus.Error} /> : null}
        <CostReviewRecommendationBanner
          isApplyingRecommendations={isApplyingRecommendations}
          selectAllRecommendations={selectAllRecommendations}
          totalAppliedSavings={totalAppliedSavings}
          totalRecommendationSavings={recommendationSummary?.totalSavings}
        />
        <CostReviewModalBodyContent
          closeOrderDetailsModal={closeOrderDetailsModal}
          costWarnings={costWarnings}
          error={error}
          insurancePurchaseAmountChanged={insurancePurchaseAmountChanged}
          insuranceRecommendations={insuranceRecommendations}
          insurances={insurances}
          isToggleRecommendationDisabled={isApplyingRecommendations}
          localDeliveryShipmentIsPresent={localDeliveryShipmentIsPresent}
          onRetry={onRetry}
          onToggleRateShopperRecommendation={toggleRateShopperRecommendation}
          providerPurchaseAmountChanged={providerPurchaseAmountChanged}
          providers={providers}
          rateShopperRecommendations={rateShopperRecommendations}
          selectedInsuranceRecommendations={selectedInsuranceRecommendations}
          selectedRateShopperRecommendations={selectedRateShopperRecommendations}
          selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
          shipFromCount={shipFromCount}
          shippingServiceRecommendations={shippingServiceRecommendations}
          shouldShowError={shouldShowError}
          toggleInsuranceRecommendation={toggleInsuranceRecommendation}
          toggleShippingServiceRecommendation={toggleShippingServiceRecommendation}
          userHasBillingInfoSetup={userHasBillingInfoSetup}
          walletedProviderGroups={walletedProviderGroups}
          walletPurchaseAmountChanged={walletPurchaseAmountChanged}
        />
        {isUpsTermsEnabled && upsTermsRequired && hasUpsProviderInCosts ? (
          <UpsTermsCheckbox checked={upsTermsAccepted} onChange={setUpsTermsAccepted} />
        ) : null}
      </ModalBody>
      <CostReviewModalFooter
        combinedCostInfo={combinedCostInfo}
        creditCardProcessingFee={creditCardProcessingFee}
        creditCardProcessingFeePercentage={creditCardProcessingFeePercentage}
        onShipDateChanged={onShipDateChanged}
        shipDate={shipDate}
        transactionPreviewProps={transactionPreviewProps}
        userDateFormat={userDateFormat}
      >
        <ModalFooterCancelButton />
        {hasSelectedRecommendations ? (
          <Button disabled={isApplyingRecommendations} onClick={applySelectedRecommendations} theme='primary'>
            <FormattedMessage {...messages.applySavings} />
          </Button>
        ) : (
          <Button disabled={shouldDisableContinue} onClick={onSubmit} theme='primary'>
            <FormattedMessage {...messages.continue} />
          </Button>
        )}
      </CostReviewModalFooter>
    </>
  );
};
